(() => {
  'use strict';
  if (customElements.get('rrd-trofast-picker')) return;
  const patterns = { stripe: 'Stripe', gingham: 'Gingham', checker: 'Checkerboard', scallopedStripe: 'Scalloped stripe', solid: 'Solid' };
  const fullPatterns = ['stripe', 'gingham', 'checker', 'scallopedStripe'];
  const styles = { full: 'Full height', scallop: 'Scallop top' };
  const normalPattern = (style, pattern) => style === 'scallop' ? 'solid' : fullPatterns.includes(pattern) ? pattern : 'stripe';

  function texture(pattern, colour) {
    // Seamless vector repeat traced from the supplied 20 cm scalloped-stripe tile.
    if (pattern === 'scallopedStripe') {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 409.6 244"><rect width="409.6" height="244" fill="white"/><path d="M112 0L109 4L102 8L96 12L91 16L86 20L82 24L78 28L74 32L71 36L68 40L66 44L63 48L61 52L59 56L57 60L55 64L54 68L52 72L51 76L50 80L49 84L48 88L47 92L46 96L46 100L45 104L45 108L44 112L44 116L44 120L44 124L44 128L45 132L45 136L45 140L46 144L47 148L47 152L48 156L49 160L50 164L51 168L53 172L54 176L56 180L57 184L59 188L61 192L63 196L66 200L69 204L72 208L75 212L78 216L82 220L87 224L91 228L97 232L104 236L111 240L112 244L289 244L289 240L295 236L302 232L308 228L312 224L317 220L321 216L324 212L328 208L330 204L333 200L336 196L338 192L340 188L342 184L343 180L345 176L347 172L348 168L349 164L350 160L351 156L352 152L353 148L353 144L354 140L354 136L354 132L355 128L355 124L355 120L355 116L354 112L354 108L354 104L353 100L353 96L352 92L351 88L350 84L349 80L348 76L347 72L345 68L344 64L342 60L340 56L338 52L336 48L334 44L331 40L328 36L325 32L321 28L318 24L313 20L308 16L303 12L297 8L290 4L289 0Z" fill="${colour}"/></svg>`;
      return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
    }
    const size = { stripe: 40, gingham: 20, checker: 80, solid: 10 }[pattern];
    const half = size / 2;
    let shape = `<rect x="${half}" width="${half}" height="${size}" fill="${colour}"/>`;
    if (pattern === 'checker') shape = `<path d="M${half} 0h${half}v${half}H${half}zM0 ${half}h${half}v${half}H0z" fill="${colour}"/>`;
    if (pattern === 'gingham') shape = `<rect width="10" height="10" fill="${colour}" fill-opacity=".8"/><rect y="10" width="10" height="10" fill="${colour}"/><rect x="10" y="10" width="10" height="10" fill="${colour}" fill-opacity=".5"/>`;
    if (pattern === 'solid') shape = `<rect width="10" height="10" fill="${colour}"/>`;
    return `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="white"/>${shape}</svg>`)}")`;
  }

  const layouts = { mixed: ['small', 'medium'], small: ['small', 'small', 'small'], large: ['large'] };
  const sizeNames = { small: 'Small', medium: 'Medium', large: 'Large' };
  const weights = { small: 1, medium: 2, large: 3 };
  const positions = ['Left', 'Centre', 'Right'];
  const normalTitle = title => String(title).toLowerCase().replace(/\s+/g, ' ').trim();

  class TrofastPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.abort = new AbortController();
      this.formId = this.dataset.formId;
      this.propertyInputs = new Map();
      this.boundForms = new WeakSet();
      this.busy = false;
      document.addEventListener('submit', event => this.handleSubmit(event), { capture: true, signal: this.abort.signal });
      try {
        this.config = JSON.parse(this.querySelector('[data-rrd-config]').textContent);
        if (!Array.isArray(this.config.variants) || !Array.isArray(this.config.palettes) || this.config.palettes.length !== 4) throw new Error('Invalid configuration');
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.config.currency || 'GBP' });
        this.colours = new Map();
        this.config.palettes.forEach(palette => {
          if (!['pastel', 'primary', 'heritage', 'neutral'].includes(palette.id) || palette.colours.length !== 4) throw new Error('Invalid palette');
          palette.colours.forEach((colour, i) => {
            if (typeof colour.name !== 'string' || !colour.name.trim() || !/^#[0-9a-f]{6}$/i.test(colour.hex)) throw new Error('Invalid colour');
            this.colours.set(`${palette.id}:${i}`, { ...colour, collection: palette.id, collectionName: palette.name });
          });
        });
        this.collection = this.colours.has(`${this.config.initialCollection}:0`) ? this.config.initialCollection : 'pastel';
        this.mode = 'matching';
        this.active = 0;
        this.columns = ['mixed', 'mixed', 'mixed'];
        this.entries = [];
        this.createColours();
        this.rebuildLayout();
        this.addEventListener('click', event => this.handleClick(event), { signal: this.abort.signal });
        this.addEventListener('change', event => {
          if (event.target.matches('[data-rrd-column]') && !this.busy) {
            const value = event.target.value;
            if (!Object.hasOwn(layouts, value)) return;
            this.columns[Number(event.target.dataset.rrdColumn)] = value;
            this.rebuildLayout();
            this.querySelector('[data-rrd-added]').hidden = true;
            this.update(true);
          }
        }, { signal: this.abort.signal });
        this.addEventListener('input', event => {
          if (event.target.matches('[data-rrd-quantity]')) this.updateOrderAvailability();
        }, { signal: this.abort.signal });
        window.addEventListener('pageshow', () => { this.attachForm(); this.update(); }, { signal: this.abort.signal });
        this.formObserver = new MutationObserver(() => this.attachForm());
        this.formObserver.observe(this.closest('product-information') || this.parentElement, { childList: true, subtree: true });
        this.attachForm();
        this.update();
      } catch (error) {
        this.showError('The picker could not load. Please refresh the page or contact us for help.');
        if (this.config?.designMode) console.warn('Trofast picker:', error.message);
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.formObserver?.disconnect();
      this.initialised = false;
    }

    get form() { return document.getElementById(this.formId); }
    get quantityInput() { return this.querySelector('[data-rrd-quantity]'); }
    get quantity() { return Number(this.quantityInput.value); }
    counts() { return this.entries.reduce((count, entry) => { count[entry.size]++; return count; }, {small: 0, medium: 0, large: 0}); }
    layoutTitle() { const count = this.counts(); return Object.keys(sizeNames).filter(size => count[size]).map(size => `${count[size]} ${size}`).join(' + '); }
    label(entry) { return `${positions[entry.column]} ${entry.slot + 1} (${entry.size})`; }
    positionLabel(entry) {
      const count = layouts[this.columns[entry.column]].length;
      const row = count === 1 ? '' : entry.slot === 0 ? 'Top ' : entry.slot === count - 1 ? 'Bottom ' : 'Middle ';
      return `${row}${positions[entry.column].toLowerCase()} · ${entry.size} tub`;
    }
    variant() {
      const matches = this.config.variants.filter(variant => normalTitle(variant.title) === this.layoutTitle());
      if (matches.length === 1) return matches[0];
      // The existing one-variant product remains usable for its original layout only.
      if (this.layoutTitle() === '3 small + 3 medium' && this.config.variants.length === 1 && normalTitle(this.config.variants[0].title) === 'default title') return this.config.variants[0];
      return null;
    }
    quantityValid(variant = this.variant()) {
      if (!variant) return false;
      const min = Number(variant.min) || 1;
      const step = Number(variant.increment) || 1;
      return Number.isSafeInteger(this.quantity) && this.quantity >= min && (!variant.max || this.quantity <= Number(variant.max)) && (this.quantity - min) % step === 0;
    }
    validState() {
      if (!Array.isArray(this.columns) || this.columns.length !== 3 || !this.columns.every(column => Object.hasOwn(layouts, column))) return false;
      const expected = this.columns.flatMap((column, c) => layouts[column].map((size, slot) => ({ size, column: c, slot })));
      return this.entries.length === expected.length && this.entries.every((entry, i) =>
        entry.size === expected[i].size && entry.column === expected[i].column && entry.slot === expected[i].slot &&
        this.colours.has(entry.colour) && Object.hasOwn(styles, entry.style) && entry.pattern === normalPattern(entry.style, entry.pattern));
    }
    orderReady() {
      const variant = this.variant();
      return !this.busy && this.validState() && variant?.available === true && this.quantityValid(variant) && String(this.form?.elements.namedItem('id')?.value) === String(variant.id);
    }
    attachForm() {
      const form = this.form;
      if (!form || this.boundForms.has(form)) return;
      this.boundForms.add(form);
      form.addEventListener('formdata', event => {
        if (!this.validState()) return;
        for (const key of Array.from(event.formData.keys())) if (/^properties\[(Bundle|Layout|Left |Centre |Right )/.test(key)) event.formData.delete(key);
        for (const [name, value] of this.properties()) event.formData.set(`properties[${name}]`, value);
      }, { signal: this.abort.signal });
      form.addEventListener('rrd:cart-added', () => {
        this.querySelector('[data-rrd-added]').hidden = false;
      }, { signal: this.abort.signal });
      form.addEventListener('rrd:cart-settled', () => { this.busy = false; this.update(); }, { signal: this.abort.signal });
      this.updateOrderAvailability();
    }
    updateOrderAvailability() {
      if (!this.validState()) return;
      const variant = this.variant();
      const input = this.form?.elements.namedItem('id');
      if (input) input.value = variant ? String(variant.id) : '';
      this.quantityInput.min = variant?.min || 1;
      this.quantityInput.step = variant?.increment || 1;
      if (variant?.max) this.quantityInput.max = variant.max;
      else this.quantityInput.removeAttribute('max');
      const ready = this.orderReady();
      this.form?.querySelectorAll('[data-add-to-cart]').forEach(button => {
        button.disabled = !ready;
        button.setAttribute('aria-disabled', String(!ready));
        const text = button.querySelector('[data-add-to-cart-text]');
        if (text && !this.busy) text.textContent = variant?.available === false ? 'Sold out' : 'Add set to basket';
      });
      const count = this.counts();
      const estimate = this.layoutTitle() === '3 small + 3 medium' ? 2500 : count.small * 400 + count.medium * 600 + count.large * 800;
      const price = variant ? Number(variant.price) : this.config.currency === 'GBP' ? estimate : null;
      const qty = Number.isSafeInteger(this.quantity) && this.quantity > 0 ? this.quantity : 1;
      this.querySelector('[data-rrd-total]').textContent = price === null ? 'Unavailable' : this.money.format(price * qty / 100);
      this.querySelector('[data-rrd-price-label]').textContent = variant ? (qty === 1 ? 'Set total' : `${qty} sets total`) : 'Preview estimate';
      this.querySelector('[data-rrd-unit-price]').textContent = price === null ? '' : `${this.money.format(price / 100)} per set · ${this.layoutTitle()}`;
      if (!variant) this.showError(this.config.designMode ? `Add a Shopify Layout variant named “${this.layoutTitle()}” to enable ordering this set. The estimate uses £4 small, £6 medium and £8 large.` : 'This layout is not available to order yet. Please choose another layout or contact us.');
      else if (!variant.available) this.showError('This layout is currently sold out.');
      else if (!this.quantityValid(variant)) this.showError('Please enter a valid whole number of sets within the available quantity range.');
      else this.querySelector('[data-rrd-error]').hidden = true;
    }
    showError(message) {
      const error = this.querySelector('[data-rrd-error]');
      error.textContent = message;
      error.hidden = false;
    }
    handleSubmit(event) {
      if (event.target !== this.form) return;
      if (!this.orderReady()) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (!this.busy) { this.showError('Please check the layout, availability and number of sets before adding to your basket.'); this.querySelector('[data-rrd-error]').focus(); }
        return;
      }
      this.syncProperties();
      this.busy = true;
      // Leave form inputs enabled until Honey has collected FormData.
      this.querySelectorAll('button,select').forEach(control => { control.disabled = true; });
      this.quantityInput.readOnly = true;
      this.querySelector('[data-rrd-added]').hidden = true;
    }
    rebuildLayout() {
      const old = this.entries;
      const chosen = old[this.active] || { colour: `${this.collection}:0`, style: 'full', pattern: 'stripe' };
      const key = old[this.active] ? `${old[this.active].column}:${old[this.active].slot}` : '0:0';
      this.entries = this.columns.flatMap((column, c) => layouts[column].map((size, slot) => {
        const prior = old.find(entry => entry.column === c && entry.slot === slot && entry.size === size);
        const design = this.mode === 'matching' ? chosen : prior || chosen;
        return { colour: design.colour, style: design.style, pattern: design.pattern, size, column: c, slot };
      }));
      this.active = Math.max(0, this.entries.findIndex(entry => `${entry.column}:${entry.slot}` === key));
      const cabinet = this.querySelector('[data-rrd-cabinet]');
      cabinet.replaceChildren();
      const row = [1, 1, 1];
      this.entries.forEach((entry, i) => {
        const tub = document.createElement('button'); tub.type = 'button'; tub.className = 'rrd-trofast__tub'; tub.dataset.rrdTub = i;
        tub.style.gridColumn = entry.column + 1; tub.style.gridRow = `${row[entry.column]} / span ${weights[entry.size]}`; row[entry.column] += weights[entry.size];
        const decal = document.createElement('span'); decal.className = 'rrd-trofast__decal';
        decal.setAttribute('aria-hidden', 'true');
        tub.append(decal); cabinet.append(tub);
      });
      this.createProperties();
    }
    createProperties() {
      const root = this.querySelector('[data-rrd-properties]'); root.replaceChildren(); this.propertyInputs.clear();
      for (const [name] of this.properties()) {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${name}]`; input.setAttribute('form', this.formId);
        this.propertyInputs.set(name, input); root.append(input);
      }
    }
    description(entry) {
      const colour = this.colours.get(entry.colour);
      return `${styles[entry.style]} / ${patterns[entry.pattern]} / ${colour.name} (${colour.collectionName})`;
    }
    properties() {
      return [['Bundle', `${this.layoutTitle()} stickers — one unit`], ['Layout', this.columns.map((layout, i) => `${positions[i]}: ${layouts[layout].join(' + ')}`).join('; ')], ...this.entries.map(entry => [this.label(entry), this.description(entry)])];
    }
    syncProperties() {
      if (!this.validState()) return;
      for (const [name, value] of this.properties()) this.propertyInputs.get(name).value = value;
    }
    setEntry(patch) {
      const apply = entry => { const next = { ...entry, ...patch }; next.pattern = normalPattern(next.style, next.pattern); return next; };
      if (this.mode === 'matching') this.entries = this.entries.map(apply);
      else this.entries[this.active] = apply(this.entries[this.active]);
    }
    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled || this.busy) return;
      const data = button.dataset;
      if ('rrdDefault' in data || 'rrdAnother' in data) {
        this.columns = ['mixed', 'mixed', 'mixed'];
        if ('rrdAnother' in data) {
          this.entries = []; this.mode = 'matching'; this.active = 0; this.quantityInput.value = '1';
          this.querySelector('[data-rrd-added]').hidden = true;
        }
        this.rebuildLayout();
      } else if (data.rrdMode) {
        this.mode = data.rrdMode;
        if (this.mode === 'matching') {
          const {colour, style, pattern} = this.entries[this.active];
          this.entries = this.entries.map(entry => ({...entry, colour, style, pattern}));
          this.active = 0;
        }
        this.collection = this.colours.get(this.entries[this.active].colour).collection;
      } else if (data.rrdTub !== undefined) {
        if (this.mode !== 'mix') return;
        this.active = Number(data.rrdTub);
        this.collection = this.colours.get(this.entries[this.active].colour).collection;
      } else if (Object.hasOwn(styles, data.rrdStyle)) this.setEntry({ style: data.rrdStyle });
      else if (fullPatterns.includes(data.rrdPattern) && this.entries[this.active].style === 'full') this.setEntry({ pattern: data.rrdPattern });
      else if (data.rrdCollection && this.config.palettes.some(p => p.id === data.rrdCollection)) this.collection = data.rrdCollection;
      else if (data.rrdColour && this.colours.has(data.rrdColour)) this.setEntry({ colour: data.rrdColour });
      else return;
      this.update(true);
      if ('rrdAnother' in data) { this.querySelector('[data-rrd-default]').focus(); this.scrollIntoView({block:'start',behavior:'smooth'}); }
    }
    update(announce = false) {
      if (!this.validState()) return;
      const entry = this.entries[this.active]; const colour = this.colours.get(entry.colour); const mixed = this.mode === 'mix';
      this.querySelectorAll('button,select').forEach(control => { control.disabled = this.busy; });
      this.quantityInput.readOnly = this.busy;
      const choose = (selector, key, value) => this.querySelectorAll(selector).forEach(button => button.setAttribute('aria-pressed', String(button.dataset[key] === value)));
      choose('[data-rrd-mode]', 'rrdMode', this.mode); choose('[data-rrd-style]', 'rrdStyle', entry.style); choose('[data-rrd-pattern]', 'rrdPattern', entry.pattern);
      choose('[data-rrd-colour]', 'rrdColour', entry.colour); choose('[data-rrd-collection]', 'rrdCollection', this.collection);
      this.querySelectorAll('[data-rrd-column]').forEach(select => { select.value = this.columns[Number(select.dataset.rrdColumn)]; });
      this.querySelector('[data-rrd-counts]').textContent = this.layoutTitle();
      this.querySelector('[data-rrd-tap-hint]').hidden = !mixed;
      this.querySelector('[data-rrd-editing]').textContent = mixed ? `Editing: ${this.positionLabel(entry)}` : 'Choose a look for all stickers';
      this.querySelector('[data-rrd-pattern-field]').hidden = entry.style === 'scallop';
      this.querySelector('[data-rrd-style-hint]').textContent = entry.style === 'scallop' ? 'Scallop tops come in solid colours.' : 'Choose a full-height pattern.';
      this.querySelectorAll('[data-rrd-pattern]').forEach(button => {
        button.disabled = this.busy || entry.style !== 'full';
        button.querySelector('span').style.backgroundImage = texture(button.dataset.rrdPattern, colour.hex);
      });
      this.querySelector('[data-rrd-collections]').hidden = !(this.config.allCollections || mixed);
      this.querySelectorAll('[data-rrd-colour]').forEach(button => { button.hidden = this.colours.get(button.dataset.rrdColour).collection !== this.collection; });
      this.querySelector('[data-rrd-chosen]').textContent = `${colour.name} · ${colour.collectionName}`;
      this.querySelector('[data-rrd-cabinet]').classList.toggle('rrd-trofast__cabinet--mix', mixed);
      this.querySelectorAll('[data-rrd-tub]').forEach(button => {
        const i = Number(button.dataset.rrdTub); const selected = this.entries[i]; const shade = this.colours.get(selected.colour);
        button.setAttribute('aria-pressed', String(mixed && i === this.active));
        button.disabled = this.busy || !mixed;
        button.setAttribute('aria-label', `${this.positionLabel(selected)}: ${this.description(selected)}.${mixed ? ' Select to edit.' : ''}`);
        const decal = button.querySelector('.rrd-trofast__decal');
        if (decal) { decal.dataset.pattern = selected.pattern; decal.dataset.style = selected.style; decal.style.setProperty('--rrd-colour', shade.hex); decal.style.backgroundImage = texture(selected.pattern, shade.hex); }
      });
      this.querySelector('[data-rrd-summary]').replaceChildren(...this.entries.map(selected => {
        const item = document.createElement('li'); const label = document.createElement('strong'); label.textContent = `${this.label(selected)}: `;
        item.append(label, document.createTextNode(this.description(selected))); return item;
      }));
      this.syncProperties(); this.updateOrderAvailability();
      if (announce) this.querySelector('[data-rrd-status]').textContent = `${this.layoutTitle()}. ${mixed ? this.label(entry) : 'All stickers'}: ${this.description(entry)}.`;
    }
    createColours() {
      const collectionRoot = this.querySelector('[data-rrd-collections]');
      const colourRoot = this.querySelector('[data-rrd-colours]');
      collectionRoot.replaceChildren();
      colourRoot.replaceChildren();
      this.config.palettes.forEach(palette => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.rrdCollection = palette.id;
        button.textContent = palette.name;
        collectionRoot.append(button);
        palette.colours.forEach((colour, index) => {
          const swatch = document.createElement('button');
          swatch.type = 'button';
          swatch.dataset.rrdColour = `${palette.id}:${index}`;
          swatch.setAttribute('aria-label', `${colour.name}, ${palette.name}`);
          const chip = document.createElement('span');
          chip.className = 'rrd-trofast__chip';
          chip.style.backgroundColor = colour.hex;
          chip.setAttribute('aria-hidden', 'true');
          const name = document.createElement('span');
          name.className = 'rrd-trofast__colour-name';
          name.textContent = colour.name;
          swatch.append(chip, name);
          colourRoot.append(swatch);
        });
      });
    }

  }
  customElements.define('rrd-trofast-picker', TrofastPicker);
})();

